import type {
  GridSelection,
  Row,
  TimeGridEntity,
  TimeGridEvent,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import {
  fromCellKey,
  getColumnCellCoords,
  getRowCellCoords,
  toCellKey,
} from 'modules/shift_management/utils/TimeGridTable/cellCoordinates'

function buildSingleCellSelection(cell: { entityId: string; slotIndex: number }): GridSelection {
  return {
    kind: 'cells',
    keys: new Set([toCellKey(cell.entityId, cell.slotIndex)]),
    anchor: cell,
  }
}

function toggleCellSelection(
  previousSelection: GridSelection,
  cell: { entityId: string; slotIndex: number }
): GridSelection {
  const clickedKey = toCellKey(cell.entityId, cell.slotIndex)

  if (previousSelection.kind !== 'cells') {
    return buildSingleCellSelection(cell)
  }

  const nextKeys = new Set(previousSelection.keys)
  if (nextKeys.has(clickedKey)) {
    nextKeys.delete(clickedKey)
  } else {
    nextKeys.add(clickedKey)
  }

  if (nextKeys.size === 0) return { kind: 'none' }

  const currentAnchorKey = toCellKey(
    previousSelection.anchor.entityId,
    previousSelection.anchor.slotIndex
  )
  const anchor = nextKeys.has(currentAnchorKey)
    ? previousSelection.anchor
    : fromCellKey(nextKeys.has(clickedKey) ? clickedKey : (Array.from(nextKeys)[0] ?? clickedKey))

  return {
    kind: 'cells',
    keys: nextKeys,
    anchor,
  }
}

function buildCellRangeSelection<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  anchorCell,
  targetCell,
  append,
  previousSelection,
  rows,
  rowIndexByEntityId,
  isCellSelectable,
}: {
  anchorCell: { entityId: string; slotIndex: number }
  targetCell: { entityId: string; slotIndex: number }
  append: boolean
  previousSelection: GridSelection
  rows: Row<TEntity, TEvent>[]
  rowIndexByEntityId: Map<string, number>
  isCellSelectable: (entityId: string, slotIndex: number) => boolean
}): GridSelection {
  const anchorRowIndex = rowIndexByEntityId.get(anchorCell.entityId)
  const targetRowIndex = rowIndexByEntityId.get(targetCell.entityId)

  if (anchorRowIndex === undefined || targetRowIndex === undefined) return previousSelection

  const minRowIndex = Math.min(anchorRowIndex, targetRowIndex)
  const maxRowIndex = Math.max(anchorRowIndex, targetRowIndex)
  const minSlotIndex = Math.min(anchorCell.slotIndex, targetCell.slotIndex)
  const maxSlotIndex = Math.max(anchorCell.slotIndex, targetCell.slotIndex)
  const nextKeys =
    append && previousSelection.kind === 'cells'
      ? new Set(previousSelection.keys)
      : new Set<string>()

  for (let rowIndex = minRowIndex; rowIndex <= maxRowIndex; rowIndex += 1) {
    const row = rows[rowIndex]
    if (!row) continue

    for (let slotIndex = minSlotIndex; slotIndex <= maxSlotIndex; slotIndex += 1) {
      if (!isCellSelectable(row.entity.id, slotIndex)) continue
      nextKeys.add(toCellKey(row.entity.id, slotIndex))
    }
  }

  if (nextKeys.size === 0) return { kind: 'none' }

  return {
    kind: 'cells',
    keys: nextKeys,
    anchor: anchorCell,
  }
}

function buildRowCellsExceptSelection({
  rowId,
  excludedSlotIndex,
  slots,
  isCellSelectable,
}: {
  rowId: string
  excludedSlotIndex: number
  slots: TimeSlot[]
  isCellSelectable: (entityId: string, slotIndex: number) => boolean
}): GridSelection {
  const cells = getRowCellCoords(rowId, slots.length).filter(
    (cell) =>
      cell.slotIndex !== excludedSlotIndex && isCellSelectable(cell.entityId, cell.slotIndex)
  )
  const keys = new Set(cells.map((cell) => toCellKey(cell.entityId, cell.slotIndex)))

  if (keys.size === 0) return { kind: 'none' }

  const fallbackSlot = excludedSlotIndex === 0 ? 1 : 0
  const fallbackKey = toCellKey(rowId, fallbackSlot)
  const firstKey = Array.from(keys)[0]
  if (!firstKey) return { kind: 'none' }

  return {
    kind: 'cells',
    keys,
    anchor: fromCellKey(keys.has(fallbackKey) ? fallbackKey : firstKey),
  }
}

function buildColumnCellsExceptSelection<
  TEntity extends TimeGridEntity,
  TEvent extends TimeGridEvent,
>({
  columnIndex,
  excludedRowId,
  rows,
  isCellSelectable,
}: {
  columnIndex: number
  excludedRowId: string
  rows: Row<TEntity, TEvent>[]
  isCellSelectable: (entityId: string, slotIndex: number) => boolean
}): GridSelection {
  const cells = getColumnCellCoords(columnIndex, rows).filter(
    (cell) => cell.entityId !== excludedRowId && isCellSelectable(cell.entityId, cell.slotIndex)
  )
  const keys = new Set(cells.map((cell) => toCellKey(cell.entityId, cell.slotIndex)))

  if (keys.size === 0) return { kind: 'none' }

  const anchor = cells[0]
  if (!anchor) return { kind: 'none' }

  return {
    kind: 'cells',
    keys,
    anchor,
  }
}

function buildColumnSelection<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  columnIndex,
  rows,
  selectableRowCount,
  isCellSelectable,
}: {
  columnIndex: number
  rows: Row<TEntity, TEvent>[]
  selectableRowCount: number
  isCellSelectable: (entityId: string, slotIndex: number) => boolean
}): { selection: GridSelection; selectsWholeColumn: boolean } | null {
  const selectableEntityIds: string[] = []

  for (const row of rows) {
    if (isCellSelectable(row.entity.id, columnIndex)) {
      selectableEntityIds.push(row.entity.id)
    }
  }

  if (selectableEntityIds.length === 0) return null

  if (selectableEntityIds.length === selectableRowCount) {
    return {
      selection: { kind: 'column', columnIndex },
      selectsWholeColumn: true,
    }
  }

  return {
    selection: {
      kind: 'cells',
      keys: new Set(selectableEntityIds.map((entityId) => toCellKey(entityId, columnIndex))),
      anchor: { entityId: selectableEntityIds[0] ?? '', slotIndex: columnIndex },
    },
    selectsWholeColumn: false,
  }
}

function buildSelectedRowIds<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  selection,
  rows,
}: {
  selection: GridSelection
  rows: Row<TEntity, TEvent>[]
}): Set<string> | null {
  if (selection.kind === 'row') return new Set([selection.rowId])

  if (selection.kind === 'rowRange') {
    const rowIds = new Set<string>()

    for (let index = selection.startIndex; index <= selection.endIndex; index += 1) {
      const row = rows[index]
      if (row && row.entity.selectable !== false) {
        rowIds.add(row.entity.id)
      }
    }

    return rowIds
  }

  return null
}

function buildSelectedColumnIndexes(selection: GridSelection): Set<number> | null {
  if (selection.kind === 'column') return new Set([selection.columnIndex])

  if (selection.kind === 'columnRange') {
    const columnIndexes = new Set<number>()

    for (let index = selection.startIndex; index <= selection.endIndex; index += 1) {
      columnIndexes.add(index)
    }

    return columnIndexes
  }

  return null
}

export {
  buildSelectedColumnIndexes,
  buildSelectedRowIds,
  buildSingleCellSelection,
  toggleCellSelection,
  buildCellRangeSelection,
  buildRowCellsExceptSelection,
  buildColumnCellsExceptSelection,
  buildColumnSelection,
}
